/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import {
    FormControl,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Select,
    SelectProps,
    styled,
} from "@mui/material";
import useErrors from "../../Utils/userErrors";
import ErrorText from "../ErrorText";
import withTooltip from "../HOC/withTooltip";
import InputBaseProps from "../InputBase/InputBaseProps";

type ForSimpleSelect = "value" | "onChange" | "children" | "autoFocus" | "disabled" | "sx" | "onBlur" | "startAdornment" | "onOpen";

type SimpleSelectProps = Pick<SelectProps, ForSimpleSelect> & InputBaseProps;

const Placeholder = styled("span")(({ theme }) => ({
    opacity: theme.palette.action.disabledOpacity,
}));

export default withTooltip(function SimpleSelect(props: SimpleSelectProps) {
    const { children, caption, placeholder, typBezeichnung, feldBezeichnung, onChange, readonly, ...rest } =
        props;
    const { error, errorTexts, clearErrorsOnChange } = useErrors(props);

    const valueChildrenMap = React.useMemo(
        () =>
            React.Children.toArray(children).reduce<Map<unknown, React.ReactNode | undefined>>(
                (acc, c) => {
                    const props: any = (c as any).props ?? {};
                    if (!("children" in props) || !("value" in props)) {
                        return acc;
                    }

                    acc.set(props.value, props.children);
                    return acc;
                },
                new Map<unknown, React.ReactNode | undefined>()
            ),
        [children]
    );

    const renderPlaceholder = React.useCallback(() => {
        if (!placeholder) {
            return "";
        }

        return <Placeholder>{placeholder}</Placeholder>;
    }, [placeholder]);

    const renderValue = (v: unknown) => (!v ? renderPlaceholder() : valueChildrenMap.get(v));

    return (
        <FormControl
            fullWidth
            variant="outlined"
            margin="dense"
            error={error}
            sx={{ mb: 0.5, mt: 1 }}
        >
            <InputLabel required={rest.required} shrink>{caption}</InputLabel>

            <Select
                MenuProps={{ className: "lan-simpleselect__auswahl-clarity" }}
                variant="outlined"
                {...rest}
                disabled={readonly}
                onChange={clearErrorsOnChange(onChange)}
                input={<OutlinedInput className="lan-simpleselect__input-clarity" label={caption} notched />}
                displayEmpty
                renderValue={renderValue}
                size="small"
                sx={props.sx}
            >
                {children}
            </Select>

            <FormHelperText>
                <ErrorText errors={errorTexts} />
            </FormHelperText>
        </FormControl>
    );
});
